import { CONSORCIO_BLUE, CONSORCIO_BLUE_HOVER } from './palette'
import { lighten, type Theme, type ThemeOptions } from '@mui/material'
// Supports weights 100-900
import '@fontsource-variable/inter'
import '@fontsource-variable/overpass-mono'

const typographies1 = [
  'body1',
  'body2',
  'button',
  'caption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'overline',
  'subtitle1',
  'subtitle2',
]

const typographies2 = ['code1', 'code2', 'code3']

// The app uses exactly TWO emphasis weights and neither had a name, so both were written as numbers
// wherever they were needed (review of PR #105). They are not interchangeable:
//
//   600 → already a token: Meridian redefines `fontWeightBold` as 600, so in this design system
//         "bold" IS 600. Every hardcoded 600 was re-declaring a token the theme already had.
//   700 → NOT in the design system. It is CSS bold, and it has to survive Meridian saying bold
//         means 600.
//
// The two are also the two emphasis weights of Consorcio's design file (`consorcio_seguros.sketch`,
// §107): Open Sans SemiBold (600) and Bold (700), with the panel title at 24px Bold — the same value
// the carto2 measurement gave (§62/§75 parity).
//
// Hence a single new token for the heavier one, named so it cannot be confused with `fontWeightBold`.
const FONT_WEIGHT_STRONG = 700

declare module '@mui/material/styles/createTypography' {
  interface FontStyle {
    fontWeightStrong: number
  }
}

// Only WEIGHTS are normalised here. The FAMILY is a live question, not an oversight: the design
// file specifies Open Sans everywhere (324 of 324 text layers), carto2 ships plain `sans-serif` and
// this app renders Inter, which is Meridian's. Three different answers, so it is a product decision
// and it moves every glyph in the app — backlog #18 `pendiente-diseño` (§62, §107).

// Consorcio's corporate blue comes from `config/palette.ts`, which is also where the tooltip box and
// the marker SVGs read it from — none of those can see a theme, and the app used to paint the same
// blue heading two different ways because each had its own copy.
//
// Meridian's default here was #036FE2 with a navy #162945 bar; the white bar with blue text is
// carto2's (§51, §61).

// Form-field colours, declared by the design file (`consorcio_seguros.sketch`, symbols
// `form/input/large/*`): the rule under the field is `#e4e5e8` at rest, Consorcio blue on focus, and
// `#ff6d66` on error. That last one is also the red carto2 painted its required asterisks with —
// sampling the parity golden gave `rgb(255, 109, 102)`, and here the design DECLARES it, which is the
// agreement §108 asks for before trusting a colour off a screenshot.
const FIELD_RULE = '#e4e5e8'
const FIELD_ERROR = '#ff6d66'

// Meridian declares `MuiButton.styleOverrides.root` as a FUNCTION. MUI's `deepmerge` only recurses
// when BOTH sides are plain objects, so declaring ours as an object REPLACED the design system's
// rules instead of adding to them, silently dropping the hover/focus-visible skin of `text` and
// `outlined` buttons and the `svg path { fill: currentColor }` rule (§108). Calling it and spreading
// the result keeps them; the three intended changes are applied on top.
const mergeButtonRoot = (ds: DesignSystemTokens, props: unknown): object => {
  const meridian = ds.components?.MuiButton?.styleOverrides?.root
  return typeof meridian === 'function'
    ? (meridian as (p: unknown) => object)(props)
    : ((meridian ?? {}) as object)
}

// Undoes Meridian's required-field marker so an asterisk is an asterisk again. Meridian hides MUI's
// (`color: transparent`) and prints `"(required)"` from a `::after`; its own source calls that a
// "temporal workaround ... for a Workflows demand" and leaves a TODO to remove it, so this overrides
// a workaround, not a design decision.
const REQUIRED_ASTERISK = {
  color: FIELD_ERROR,
  '&.Mui-error': { color: FIELD_ERROR },
  '&::after': { content: '""' },
}

/**
 * The slice of the design system this file reads — and NOT `Theme`.
 *
 * What actually arrives is `cartoThemeOptions`: a plain options object. It carries a palette and a
 * typography scale, and it carries NO `spacing()`, no `mixins`, no `unstable_sx`. Declaring the
 * parameter as `Theme` is what let a `theme.spacing(2)` through in here and blanked the whole app at
 * runtime (§118); with this type that same line is a compile error.
 */
interface DesignSystemTokens {
  palette: Theme['palette']
  typography: Theme['typography']
  components?: Theme['components']
}

// The return type is `ThemeOptions`, which is what `createTheme` composes, and NOT `Theme`: the old
// `as Theme` cast claimed this object was a whole theme (it has no `spacing`, no `mixins`, no
// `unstable_sx`…) and TypeScript only tolerated it while the shapes happened to overlap enough. It
// stopped tolerating it the moment the `MuiFormLabel` override below was added, which is a fair
// warning: with the cast gone, a malformed override is now a type error instead of a surprise at
// runtime.
export const customTheme = (ds: DesignSystemTokens): ThemeOptions => ({
  typography: {
    fontFamily: 'Inter Variable, sans-serif',
    fontWeightStrong: FONT_WEIGHT_STRONG,
    ...typographies1.reduce(
      (acc, variant) => ({
        ...acc,
        [variant]: {
          fontFamily: 'Inter Variable, sans-serif',
        },
      }),
      {},
    ),
    ...typographies2.reduce(
      (acc, variant) => ({
        ...acc,
        [variant]: {
          fontFamily: 'Overpass Mono Variable, monospace',
        },
      }),
      {},
    ),
  },
  palette: {
    // Primary accent in Consorcio blue, like carto2 (not Meridian's #036FE2): titles, links,
    // buttons and tabs (visual parity §61).
    //
    // `dark` and `light` are set EXPLICITLY, and that is the whole point of them being here.
    // `createTheme(cartoThemeOptions, customTheme(...))` runs `createPalette` on its FIRST argument
    // only and deep-merges the rest afterwards, so `augmentColor` never sees this `main` and never
    // derives its shades: overriding `main` alone left `dark` at Meridian's blue[500] #024D9E, which
    // is exactly what MUI paints on a contained button's HOVER. The rejected blue came back on the
    // app's main CTA (§108). `dark` is the design's declared "Accent hover"; `light` has no design
    // value, so it follows MUI's own derivation (`lighten(main, tonalOffset)`, tonalOffset 0.2).
    primary: {
      main: CONSORCIO_BLUE,
      dark: CONSORCIO_BLUE_HOVER,
      light: lighten(CONSORCIO_BLUE, 0.2),
      contrastText: ds.palette.common.white,
    },
    // `brand` is Meridian's own palette colour, and `createTheme` MERGES it: its other entries
    // (navyBlue, locationRed, predictionBlue, softBlue) survive untouched. The type asks for all of
    // them, so the partial is cast here — a two-key cast in the one place that needs it, rather than
    // the blanket `as Theme` this file used to end with, which hid it.
    brand: {
      // A WHITE top bar with Consorcio-blue text and accent, like carto2 (not Meridian's navy
      // #162945). The header (components/header/style.ts) consumes these two tokens.
      appBarMain: ds.palette.common.white,
      appBarContrastText: CONSORCIO_BLUE,
    } as Theme['palette']['brand'],
  },
  // A look-and-feel improvement (it keeps carto2's style and makes it more appealing, §62):
  // buttons and inputs with soft corners, buttons without UPPERCASING (more legible, as in carto2)
  // and flat.
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: (props: unknown) => ({
          ...mergeButtonRoot(ds, props),
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: ds.typography.fontWeightBold,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    // The required-field marker back to carto2's: a RED ASTERISK next to the field's name.
    //
    // Meridian hides MUI's asterisk (`color: transparent`) and prints `"(required)"` from a
    // `::after` instead. That is an English word in a Spanish-only app, and it was invisible to
    // every check we have: a pseudo-element is not a text node, so neither `textContent`, nor the
    // string sweep of §115, nor L1's assertions could see it. It took someone looking at the
    // screen (owner review) to catch it.
    //
    // carto2 marked them with a red `*` right after the label — verified against the parity
    // golden (`tests/parity/visual/resultados/carto2/main__desktop.png`), where `Dep / Ofic`, the
    // only optional field, carries no mark. That is parity backlog #9; its other half (underline
    // fields with the name as a placeholder) stays parked, since that one IS invasive.
    //
    // Replacing Meridian's override rather than merging into it is the POINT here, and it works
    // for the same reason it was a bug in `MuiButton` (§108): theirs is a function, ours an object,
    // and MUI's `deepmerge` does not recurse into that, so ours wins whole. The colour is the
    // theme's own `error.main` and not a value sampled off the golden: §108 already learned that
    // canvas-sampling an antialiased glyph gives a lighter, wrong colour (the `#003aa9` that was
    // really `#003DA6`). Consorcio's design file declares its red at `#cc1500`, the same family.
    // BOTH slots, and it is not belt-and-braces: Meridian spreads the same `LabelOverrides` into
    // `MuiInputLabel` AND `MuiFormLabel`, so overriding one leaves the other painting `(required)`.
    // That is exactly what the first attempt did — the unit test passed and the browser did not
    // change, which is why the live check exists.
    MuiFormLabel: { styleOverrides: { asterisk: REQUIRED_ASTERISK } },

    // Fields as carto2 drew them, and as the design file specifies: NO box, a 1px rule underneath,
    // and the field's name INSIDE the field, floating up to a small label as soon as there is a
    // value (parity backlog #9, the half that was parked as invasive).
    //
    // `standard` is MUI's own underline variant, so this is not a hand-rolled look: what has to be
    // undone is Meridian, which pins every label above the field (`position: static;
    // transform: none`) and so kills the float. Restoring MUI's resting and shrunk transforms is
    // what puts the name back inside — and the 0.75 scale is what turns the design's 16px resting
    // label into its 12px floated one, exactly as the two symbol states draw it.
    // `variant: standard` is what gives the underline. The other half of the job — letting the label
    // REST INSIDE the field — cannot be done from here: Meridian sets
    // `MuiTextField.defaultProps.InputLabelProps = { shrink: true }`, defaultProps are merged rather
    // than replaced, and this MUI version has no `slotProps` on TextField to take precedence. So the
    // components undo it explicitly (`FLOATING_LABEL` in `pages/quote/field-props.ts`), which is also
    // where a reader will be looking when they wonder why the label moves.
    MuiTextField: { defaultProps: { variant: 'standard' } },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: REQUIRED_ASTERISK,
        standard: {
          position: 'absolute',
          marginBottom: 0,
          fontSize: 16,
          fontWeight: ds.typography.fontWeightRegular,
          color: ds.palette.text.secondary,
          transform: 'translate(0, 20px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(0, -1.5px) scale(0.75)',
            fontWeight: ds.typography.fontWeightBold,
            color: ds.palette.text.primary,
          },
        },
      },
    },
    // `&&` doubles the specificity on purpose: Meridian styles this same underline (its rule comes
    // out as its text colour at 25% alpha), and with a single `&` which one wins depends on emotion's
    // insertion order. Same lesson as the legend's overrides (§109).
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: 16,
          // The room the floating label needs above the value. MUI reserves it with this exact
          // selector and Meridian drops it, which is why the label landed ON the value: with the
          // label pinned above by Meridian there was nothing to reserve space for.
          // 16, literal: what reaches `customTheme` is `cartoThemeOptions` cast to `Theme`, which
          // has a palette but NO `spacing()` — calling it here threw at module init and took the whole
          // app down (blank page, no heading). Another thing the old blanket cast was hiding.
          'label + &&': { marginTop: 16 },
          '&&.MuiInputBase-formControl': { marginTop: 16 },
          // The design draws the value at 24px of line height inside a 64px field.
          '& .MuiInputBase-input': {
            paddingTop: 4,
            paddingBottom: 6,
            lineHeight: '24px',
          },
          '&&::before': { borderBottom: `1px solid ${FIELD_RULE}` },
          '&&:hover:not(.Mui-disabled, .Mui-error)::before': {
            borderBottom: `1px solid ${CONSORCIO_BLUE}`,
          },
          '&&::after': { borderBottom: `1px solid ${CONSORCIO_BLUE}` },
          '&&.Mui-error::after': { borderBottom: `1px solid ${FIELD_ERROR}` },
          '&&.Mui-error::before': { borderBottom: `1px solid ${FIELD_ERROR}` },
        },
      },
    },
  },
})
